import useSWR from "swr";
import { useNavigate, useParams } from "react-router-dom";
import { Chip } from "@/components/badge/chip";
import { Section } from "@/components/section";
import { RowCard } from "@/components/list/row-card";
import { RowIndex } from "@/components/list/row-index";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { useAuthStore, swrKey } from "@/lib/swr";
import {
  ROLLOUT_TASK_LABELS,
  refreshRolloutTasks,
  rolloutStepLabel,
} from "@/lib/rollout-task";
import { RolloutConfirmButton } from "@/components/versions/rollout-confirm-button";
import TaskDueInput from "@/components/task-confirm/due-input";
import { taskDue } from "@/components/task-confirm/due";
import type { RolloutTask } from "@/types/domain";

// App-wide rollout confirmation tasks: pending sign-offs on top (the work
// queue — expected to stay at zero), confirmed history below. Rows link to
// the version the task belongs to.
const AppRolloutTasks = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.auth?.user);
  const { data: tasks, isLoading } = useSWR<RolloutTask[]>(
    id ? swrKey("/rollout-tasks", { pid: id }) : null
  );

  if (!tasks?.length) {
    return isLoading ? (
      <ListLoading />
    ) : (
      <ListEmpty>
        No rollout data tasks yet — revenue data is requested at each channel
        rollout step and version data once rollout reaches 100% (apps without
        channels: on entering rollout / going online), as configured in the
        app Settings → Flow.
      </ListEmpty>
    );
  }

  const pending = tasks.filter((t) => !t.confirmedAt);
  const confirmed = tasks.filter((t) => t.confirmedAt);

  const row = (task: RolloutTask, i: number) => {
    const due = taskDue(task);
    const mine = !!user && task.assigneeUserId === user.id;
    return (
      <RowCard
        key={task.id}
        onClick={() => navigate(`/apps/${id}/versions/${task.versionId}`)}
      >
        <RowIndex n={i + 1} />
        <span className="min-w-0 flex-1 truncate">
          {task.version?.docName ?? task.versionId}
          <span className="text-muted-foreground">
            {" · "}
            {task.assignee?.name ?? "unassigned"}
          </span>
        </span>
        <Chip tone={task.type === "commercial" ? "purple" : "cyan"}>
          {ROLLOUT_TASK_LABELS[task.type]}
        </Chip>
        {rolloutStepLabel(task) && <Chip>{rolloutStepLabel(task)}</Chip>}
        {task.confirmedAt ? (
          <Chip tone="green">confirmed {task.confirmedAt.slice(0, 10)}</Chip>
        ) : mine || user?.isAdmin ? (
          <>
            {due.overdue && <Chip tone="red">overdue</Chip>}
            <TaskDueInput
              endpoint={`/rollout-tasks/${task.id}`}
              onSaved={refreshRolloutTasks}
              dueAt={task.dueAt}
              overdue={due.overdue}
            />
            {task.version && (
              <RolloutConfirmButton task={task} version={task.version} />
            )}
          </>
        ) : (
          <Chip tone={due.overdue ? "red" : "yellow"}>
            {due.overdue ? `overdue ${due.date}` : `due ${due.date}`}
          </Chip>
        )}
      </RowCard>
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <Section title={`Pending (${pending.length})`}>
        {pending.length ? (
          <div className="flex flex-col gap-1">{pending.map(row)}</div>
        ) : (
          <ListEmpty>All confirmed — nothing pending.</ListEmpty>
        )}
      </Section>
      {confirmed.length > 0 && (
        <Section title={`Confirmed (${confirmed.length})`}>
          <div className="flex flex-col gap-1">{confirmed.map(row)}</div>
        </Section>
      )}
    </div>
  );
};

export default AppRolloutTasks;
